import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Card, Typography, TextField, Select, MenuItem, FormControl,
  InputLabel, Grid, InputAdornment, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, LinearProgress, Alert, Chip, IconButton, Tooltip
} from '@mui/material';
import { Add, Search, Visibility, Edit } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { obligationsApi } from '../../services/api';
import { format, parseISO } from 'date-fns';
import type { Obligation } from '../../types';
import CreateObligationDialog from './CreateObligationDialog';
import { useUser } from '../../context/UserContext';

const OBL_TYPES = ['sla', 'milestone', 'reporting', 'maintenance', 'escalation', 'performance', 'compliance', 'other'];
const OBL_STATUSES = ['pending', 'in_progress', 'completed', 'overdue', 'waived', 'at_risk'];

function StatusChip({ status }: { status: string }) {
  const map: Record<string, any> = {
    completed: { color: 'success' }, overdue: { color: 'error' }, at_risk: { color: 'warning' },
    in_progress: { color: 'info' }, pending: { color: 'default' }, waived: { color: 'default' }
  };
  return <Chip label={status.replace('_', ' ')} color={map[status]?.color || 'default'} size="small" sx={{ textTransform: 'capitalize' }} />;
}

export default function ObligationList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { effectiveRole } = useUser();
  const canEdit = effectiveRole !== 'viewer';
  const contractIdFilter = searchParams.get('contract_id');
  const isNew = searchParams.get('new') === 'true';

  const [obligations, setObligations] = useState<Obligation[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [oblType, setOblType] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [createOpen, setCreateOpen] = useState(isNew);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, any> = { page: page + 1, limit: rowsPerPage };
      if (status) params.status = status;
      if (oblType) params.obligation_type = oblType;
      if (contractIdFilter) params.contract_id = contractIdFilter;

      const res = await obligationsApi.list(params);
      setObligations(res.data.obligations);
      setTotal(res.data.pagination.total);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load obligations');
    } finally {
      setLoading(false);
    }
  }, [status, oblType, page, rowsPerPage, contractIdFilter]);

  useEffect(() => { load(); }, [load]);

  const formatDate = (d?: string) => d ? format(parseISO(d), 'MMM d, yyyy') : '-';

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5">Obligations</Typography>
          <Typography variant="body2" color="text.secondary">{total} total obligations</Typography>
        </Box>
        {canEdit && (
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
            Add Obligation
          </Button>
        )}
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 2, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={status} label="Status" onChange={(e) => { setStatus(e.target.value); setPage(0); }}>
                <MenuItem value="">All Statuses</MenuItem>
                {OBL_STATUSES.map(s => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s.replace('_', ' ')}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select value={oblType} label="Type" onChange={(e) => { setOblType(e.target.value); setPage(0); }}>
                <MenuItem value="">All Types</MenuItem>
                {OBL_TYPES.map(t => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button variant="outlined" size="small" onClick={() => { setStatus(''); setOblType(''); setPage(0); }}>
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600 }}>Obligation</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Contract</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Due Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Assigned To</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>SLA Target</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {obligations.map((ob) => (
                <TableRow key={ob.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/obligations/${ob.id}`)}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{ob.obligation_name}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>{ob.description}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{ob.contract_name || '-'}</Typography>
                    {ob.counterparty_name && <Typography variant="caption" color="text.secondary">{ob.counterparty_name}</Typography>}
                  </TableCell>
                  <TableCell><Chip label={ob.obligation_type} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} /></TableCell>
                  <TableCell><StatusChip status={ob.status} /></TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDate(ob.due_date)}</Typography>
                    {ob.days_until_due !== null && ob.days_until_due !== undefined && ob.days_until_due <= 7 && ob.days_until_due >= 0 && (
                      <Chip label={`${ob.days_until_due}d`} color="warning" size="small" />
                    )}
                  </TableCell>
                  <TableCell><Typography variant="body2">{ob.assigned_name || '-'}</Typography></TableCell>
                  <TableCell>
                    {ob.sla_target_value != null ? (
                      <Typography variant="body2">{ob.sla_target_value}{ob.sla_unit}</Typography>
                    ) : '-'}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="View">
                      <IconButton size="small" onClick={() => navigate(`/obligations/${ob.id}`)}>
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && obligations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No obligations found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div" count={total} page={page} rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
        />
      </Card>

      <CreateObligationDialog
        open={createOpen}
        defaultContractId={contractIdFilter || undefined}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { setCreateOpen(false); load(); }}
      />
    </Box>
  );
}
